import React, { useEffect, useState } from 'react';
import { CreateUserPayload, UpdateUserPayload } from '../../types/user';
import { UserFormModalProps } from '../../types/userForm';
import styles from './CategoryFormModal.module.css';

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  roles,
  isLoading = false,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('male');
  const [roleId, setRoleId] = useState<number | ''>('');
  const [ranking, setRanking] = useState<number>(0);
  const [mobileNumber, setMobileNumber] = useState('');
  const [dateBirth, setDateBirth] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setUsername(initialData.username || '');
      setPassword('');
      setEmail(initialData.email || '');
      setFirstName(initialData.first_name || '');
      setLastName(initialData.last_name || '');
      setGender(initialData.gender?.toLowerCase() || 'male');
      setRoleId(initialData.role?.id ?? '');
      setRanking(initialData.ranking ?? 0);
      setMobileNumber(initialData.mobile_number || '');
      setDateBirth(
        initialData.dateBirth
          ? new Date(initialData.dateBirth).toISOString().slice(0, 10)
          : ''
      );
    } else {
      setUsername('');
      setPassword('');
      setEmail('');
      setFirstName('');
      setLastName('');
      setGender('male');
      setRoleId('');
      setRanking(0);
      setMobileNumber('');
      setDateBirth('');
    }
    setValidationError(null);
  }, [initialData, isOpen]);

  const validate = (): boolean => {
    if (!username.trim()) {
      setValidationError('Username is required');
      return false;
    }
    if (username.trim().length < 3 || username.trim().length > 50) {
      setValidationError('Username must be 3-50 characters');
      return false;
    }
    if (!initialData && (!password || password.length < 6)) {
      setValidationError('Password is required and must be at least 6 characters');
      return false;
    }
    if (initialData && password && password.length < 6) {
      setValidationError('Password must be at least 6 characters if provided');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setValidationError('Email is required');
      return false;
    }
    if (!emailRegex.test(email.trim())) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    if (!firstName.trim()) {
      setValidationError('First name is required');
      return false;
    }
    if (firstName.trim().length > 150) {
      setValidationError('First name must be up to 150 characters');
      return false;
    }
    if (!lastName.trim()) {
      setValidationError('Last name is required');
      return false;
    }
    if (lastName.trim().length > 150) {
      setValidationError('Last name must be up to 150 characters');
      return false;
    }
    if (!roleId) {
      setValidationError('Role is required');
      return false;
    }
    if (isNaN(ranking) || ranking < 0) {
      setValidationError('Ranking must be a non-negative number');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    if (initialData) {
      const payload: UpdateUserPayload = {
        username: username.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender,
        role_id: Number(roleId),
        ranking,
        mobile_number: mobileNumber.trim() || null,
        dateBirth: dateBirth || null,
      };
      if (password) payload.newPassword = password;
      onSubmit(payload);
    } else {
      onSubmit({
        username: username.trim().toLowerCase(),
        password,
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender,
        role_id: Number(roleId),
        ranking,
        mobile_number: mobileNumber.trim() || null,
        dateBirth: dateBirth || null,
      } as CreateUserPayload);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modalContent} ${styles.modalContentWide}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 title={initialData ? 'Edit User' : 'Add New User'}>
          {initialData ? 'Edit User' : 'Add New User'}
        </h3>
        <form onSubmit={handleSubmit}>
          {validationError && (
            <div className={styles.errorMessage} role="alert" title={validationError}>
              {validationError}
            </div>
          )}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="username" title="Username (3-50 characters)">
                Username *
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. johndoe"
                disabled={isLoading}
                title={username || 'Username'}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email" title="Email address">
                Email *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. user@example.com"
                disabled={isLoading}
                title={email || 'Email'}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="firstName" title="First name">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                title={firstName || 'First name'}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="lastName" title="Last name">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                title={lastName || 'Last name'}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="password" title={initialData ? 'Leave blank to keep current password' : 'Password (min 6 characters)'}>
              Password {initialData ? '(leave blank to keep current)' : '*'}
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={initialData ? '••••••••' : 'Min 6 characters'}
              disabled={isLoading}
              title="Password"
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="gender" title="Gender">
                Gender *
              </label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={isLoading}
                title={gender}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="role" title="User role">
                Role *
              </label>
              <select
                id="role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : '')}
                required
                disabled={isLoading}
                title={roles.find((r) => r.id === roleId)?.role_name || 'Select role'}
              >
                <option value="">Select role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id} title={role.role_name}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="ranking" title="Ranking (non-negative number)">
                Ranking *
              </label>
              <input
                type="number"
                id="ranking"
                min={0}
                value={ranking}
                onChange={(e) => setRanking(parseInt(e.target.value, 10) || 0)}
                disabled={isLoading}
                title={String(ranking)}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="mobileNumber" title="Mobile number (optional)">
                Mobile Number
              </label>
              <input
                type="tel"
                id="mobileNumber"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                placeholder="Optional"
                disabled={isLoading}
                title={mobileNumber || 'Mobile number'}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="dateBirth" title="Date of birth (optional)">
                Date of Birth
              </label>
              <input
                type="date"
                id="dateBirth"
                value={dateBirth}
                onChange={(e) => setDateBirth(e.target.value)}
                disabled={isLoading}
                title={dateBirth || 'Date of birth'}
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
              title={initialData ? 'Save changes' : 'Create user'}
            >
              {isLoading ? 'Saving...' : initialData ? 'Save Changes' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isLoading}
              title="Cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
